import { useState } from "react";
import { formatRelative } from "date-fns";
import classNames from "classnames";
import { useFormContext, useWatch } from "react-hook-form";

import styles from "./index.module.scss";
import ProfilePreview from "../ProfilePreview";
import TextArea from "../../TextArea";
import Button from "../../Button";
import TextLink from "../../TextLinks";
import { Text } from "../../Type";
import { Check } from "../../Icons/general-interface-1";
import { useStoreContext } from "../../../contexts/StoreProvider";

type PropsType = {
  section?: string;
  id: string;
  setOpen: (i: boolean) => void;
  locked?: boolean;
};

/**
 * Each CommentBox has a message, date (Date), author (UserType), read (Date) and temporary-text associated with it
 */
const CommentBox = ({ section, id, setOpen, locked }: PropsType) => {
  const {
    state: { user },
  } = useStoreContext();

  const { getValues, setValue, register } = useFormContext();
  const idDotFormat = (() => {
    if (section) {
      return `comments.${section}.${id}`;
    }
    return `${id}`;
  })();

  register(`${idDotFormat}.date`);
  register(`${idDotFormat}.message`);
  register(`${idDotFormat}.author`);
  register(`${idDotFormat}.read`);

  const [editMode, setEditMode] = useState<boolean>(
    !(
      getValues(`${idDotFormat}`) &&
      getValues(`${idDotFormat}.message`)?.length > 0
    )
  );

  const [text, setText] = useState<string>(getValues(`${idDotFormat}.message`));
  const currentText = useWatch({ name: `${idDotFormat}.temporary-text` });

  const date = (() => {
    if (getValues(`${idDotFormat}.date`)) {
      return new Date(getValues(`${idDotFormat}.date`));
    }
    return undefined;
  })();

  const readDate = (() => {
    if (getValues(`${idDotFormat}.read`)) {
      return new Date(getValues(`${idDotFormat}.read`));
    }
    return undefined;
  })();

  return (
    <div className={styles.box}>
      <div className={styles.profilePreview}>
        <ProfilePreview
          user={getValues(`${idDotFormat}.author`) || user}
          size="medium"
        />
      </div>
      <div className={styles.comment}>
        <div
          style={{ height: "180px" }}
          className={classNames(styles.textAreaFix, {
            [styles.displayNone]: !editMode,
          })}
        >
          <TextArea
            comment
            section={section}
            id={id}
            placeholder="Add Comment"
            maxLength={350}
          />
        </div>

        {!editMode && (
          <div>
            {readDate && (
              <div className={styles.readDate}>
                <Text
                  className={styles.readDateText}
                  component="span"
                  size="small"
                >
                  <Check className={styles.readCheckmark} />
                  {readDate && `Read ${formatRelative(readDate, new Date())}`}
                </Text>
              </div>
            )}

            <Text className={styles.text} component="p" size="medium">
              {text}
            </Text>
            <Text className={styles.dateText} component="p" size="small">
              {date && formatRelative(date, new Date())}
            </Text>
          </div>
        )}
      </div>

      {/* SALES REP VIEWS */}
      {user?.role?.name === "Sales Rep" && !readDate && (
        <div className={styles.buttonGroup}>
          <TextLink
            onClick={() => {
              if (editMode) {
                if (setOpen) {
                  setValue(`${idDotFormat}.temporary-text`, "");
                  setEditMode(false);
                  setOpen(false);
                }
              } else {
                setText("");
                setValue(`${idDotFormat}.read`, undefined);
                setValue(`${idDotFormat}.date`, undefined);
                setValue(`${idDotFormat}.message`, undefined);
                setValue(`${idDotFormat}.temporary-text`, "");
                setValue(`${idDotFormat}.author`, undefined);
                setEditMode(true);
                setOpen(false);
              }
            }}
            disabled={editMode && !/\S/.test(text)}
          >
            {editMode ? "Cancel" : "Delete"}
          </TextLink>
          <Button
            className={styles.button}
            secondary
            disabled={
              (editMode &&
                (currentText === undefined ||
                  currentText.length < 1 ||
                  currentText.length > 350)) ||
              (!editMode &&
                (text === undefined || text.length < 1 || text.length > 350))
            }
            onClick={() => {
              setEditMode(!editMode);
              setValue(`${idDotFormat}.date`, new Date());
              setValue(`${idDotFormat}.message`, currentText);
              setValue(`${idDotFormat}.read`, undefined);
              setValue(`${idDotFormat}.author`, user);
              setText(currentText);
            }}
          >
            {editMode ? "Comment" : "Edit Comment"}
          </Button>
        </div>
      )}

      {user?.role?.name === "Sales Rep" && readDate && (
        <div className={styles.buttonGroup}>
          <div />
          <Button
            className={styles.button}
            secondary
            onClick={() => {
              setValue(`${idDotFormat}.read`, undefined, { shouldDirty: true, });
              setValue(`${idDotFormat}.date`, undefined);
              setValue(`${idDotFormat}.message`, undefined);
              setValue(`${idDotFormat}.temporary-text`, "");
              setValue(`${idDotFormat}.author`, undefined);
              setEditMode(true);
            }}
          >
            Resolve
          </Button>
        </div>
      )}

      {["Authorizer", "Motion Support"].includes(String(user?.role?.name)) && !readDate && (
        <div className={styles.buttonGroup}>
          <div />
          <Button
            className={styles.button}
            secondary
            onClick={() => { setValue(`${idDotFormat}.read`, new Date(), { shouldDirty: true, }); }}
            disabled={locked}
          >
            Mark as Read
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommentBox;
